import React, { useState } from 'react';
import {
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import DeleteIcon from '@mui/icons-material/Delete';
import { SoaDocument, SoaReportVariant } from '@/types/soa';

interface SoaRowActionsMenuProps {
    anchorEl: null | HTMLElement;
    onClose: () => void;
    document: SoaDocument | null;
    onView: (document: SoaDocument) => void;
    onDownloadReport: (document: SoaDocument, variant: SoaReportVariant) => void;
    onDownloadEmailLog: (document: SoaDocument) => void;
    onAssign: (document: SoaDocument) => void;
    onDelete: (document: SoaDocument) => void;
}

/** One shared Menu anchored to whichever row's button was clicked, rather than
 *  per-row menu state — same pattern as UserList. */
const SoaRowActionsMenu: React.FC<SoaRowActionsMenuProps> = ({
    anchorEl, onClose, document, onView, onDownloadReport, onDownloadEmailLog, onAssign, onDelete,
}) => {
    const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);

    const closeAll = () => {
        setDownloadAnchorEl(null);
        onClose();
    };

    const run = (action: () => void) => {
        closeAll();
        action();
    };

    if (!document) return null;

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={closeAll}
                PaperProps={{
                    elevation: 3,
                    sx: { minWidth: 220, borderRadius: 2, mt: 1 },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => run(() => onView(document))}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Open statement</ListItemText>
                </MenuItem>

                <MenuItem onClick={event => setDownloadAnchorEl(event.currentTarget)}>
                    <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download report</ListItemText>
                    <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </MenuItem>

                <MenuItem onClick={() => run(() => onDownloadEmailLog(document))}>
                    <ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download email log</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => run(() => onAssign(document))}>
                    <ListItemIcon>
                        {document.client_master
                            ? <HowToRegIcon fontSize="small" />
                            : <PersonAddIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>
                        {document.client_master ? 'Reassign / re-verify client' : 'Assign client'}
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => run(() => onDelete(document))}>
                    <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'var(--neg)' }} /></ListItemIcon>
                    <ListItemText sx={{ color: 'var(--neg)' }}>Delete</ListItemText>
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={downloadAnchorEl}
                open={!!downloadAnchorEl}
                onClose={() => setDownloadAnchorEl(null)}
                PaperProps={{ elevation: 3, sx: { minWidth: 230, borderRadius: 2 } }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <MenuItem onClick={() => run(() => onDownloadReport(document, 'two_page_disclaimer'))}>
                    <ListItemText>2 Pages + Disclaimer</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => run(() => onDownloadReport(document, 'full_report'))}>
                    <ListItemText>Full Report</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

export default SoaRowActionsMenu;
